import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from 'app/layouts/DefaultLayout';
import MainContentLayout from 'app/layouts/MainContentLayout';
import Path from 'app/route/Path';
import LoadingProgress from 'app/components/commonUI/LoadingProgress';
import NoData from 'app/components/commonUI/NoData';
import GA, { EVENT_NAMES } from 'app/services/googleAnalytics';
import { toast } from 'react-toastify';
import InputField from 'app/components/commonUI/InputField';
import SelectField from 'app/components/commonUI/SelectField';
import MenuCard from './components/MenuCard';
import ConfirmDeleteDialog from 'app/components/CommonDialog/ConfirmDeleteDialog';
import NavigationBackBtn from '../../../components/common/NavigationBackButton';
import { getAllMenus, getMenuById, deleteMenu } from 'app/services/api';
import { MENU_CATEGORY_OPTIONS } from 'app/services/api/fake_services/menu_management_fake_data';
import useDebounce from '../../../hooks/useDebounce';

const SearchIcon = () => (
  <span className="material-symbols-outlined absolute -left-1 top-1/2 -translate-y-1/2 text-slate-400 text-xl">
    search
  </span>
);

const FilterIcon = () => (
  <span className="material-symbols-outlined absolute top-1/2 -translate-y-1/2 text-slate-400 text-xl">
    filter_list
  </span>
);

function MenuManagementTopPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [menus, setMenus] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [searchName, setSearchName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [selectedMenuName, setSelectedMenuName] = useState('');
  const debouncedSearchName = useDebounce(searchName, 500);

  useEffect(() => {
    setSearchName(debouncedSearchName);
  }, [debouncedSearchName]);

  const loadMenus = async (page = 1) => {
    setIsLoading(true);
    try {
      const res = await getAllMenus({
        page,
        limit: 10,
        searchName: debouncedSearchName,
        category: selectedCategory,
      });
      const data = res.data;
      setMenus(data?.salonMenus);
      setPagination(data?.pagination);
    } catch (error) {
      console.error('Load menus error:', error);
      toast.error('メニュー取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMenus(1);
  }, [debouncedSearchName, selectedCategory]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadMenus(newPage);
    }
  };

  const handleAddMenu = () => {
    GA.tracking(EVENT_NAMES.MENU_LIST?.CLICK_CREATE);
    navigate(Path.menuManagementCreate);
  };

  const handleDeleteMenu = (id, name) => {
    setSelectedMenuId(id);
    setSelectedMenuName(name);
    setShowDeleteDialog(true);
  };

  const handleCopyMenu = async (menuId) => {
    try {
      const res = await getMenuById(menuId);

      if (res.data) {
        GA.tracking(EVENT_NAMES.MENU_LIST?.CLICK_COPY, { menuId });
        navigate(Path.menuManagementCreate, {
          state: {
            copyData: {
              ...res.data,
              name: res.data.name + '_コピー',
              publicStatus: false,
              showOnBookingSite: false,
            },
          },
        });
      } else {
        toast.error('メニューが見つかりません');
      }
    } catch (error) {
      console.error('Copy menu error:', error);
      toast.error('メニュー取得に失敗しました');
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteMenu(selectedMenuId);

      if (response.success) {
        toast.success('メニューを削除しました');
        GA.tracking(EVENT_NAMES.MENU_LIST.DELETE, {
          menuId: selectedMenuId,
        });

        loadMenus(pagination.currentPage);
      } else {
        toast.error('メニューの削除に失敗しました');
      }
    } catch (error) {
      console.error('Delete menu error:', error);
      toast.error('メニューの削除に失敗しました');
    } finally {
      setSelectedMenuId(null);
      setSelectedMenuName('');
    }
  };

  const handleSearchNameChange = (value) => {
    setSearchName(value);
  };

  return (
    <DefaultLayout>
      <MainContentLayout
        pageTitle={
          <>
            <p>メニュー管理</p>
          </>
        }
        onClick={() => navigate(Path.home)}
      >
        <div className="w-full h-full pb-8 lg:pb-12 relative">
          <div className="w-full h-8 lg:h-12 flex items-center justify-start inset-x-0 top-0">
            <h2 className="text-base lg:text-xl font-semibold">
              <NavigationBackBtn url={Path.home} text={'メニュー管理'} />
            </h2>
          </div>

          <div className="w-full h-full flex flex-col px-4 py-6 md:px-0">
            <div className="space-y-4 mb-8">
              <button
                onClick={handleAddMenu}
                className="w-full bg-blue-btn-primary text-white h-12 rounded-xl font-bold flex items-center justify-center gap-2 shadow-sm shadow-primary/20 active:scale-[0.98] transition-transform"
              >
                <span className="material-symbols-outlined text-xl">add</span>
                新規メニュー登録
              </button>

              <div className="space-y-3">
                <div className="mb-3">
                  <InputField
                    placeholder="メニュー名で検索"
                    inputClasses="rounded-xl pl-10 pr-4 bg-slate-50 border-slate-200 h-11 text-sm focus:ring-blue-btn-primary focus:border-blue-btn-primary"
                    prefixIcon={<SearchIcon />}
                    name="search"
                    value={searchName}
                    onChange={handleSearchNameChange}
                  />
                </div>

                <div>
                  <SelectField
                    name="category"
                    inputWrapClasses="w-full"
                    inputClasses="w-full bg-slate-50 border-slate-200 rounded-xl h-11 text-sm focus:ring-blue-btn-primary focus:border-blue-btn-primary"
                    value={selectedCategory}
                    setValue={(value) => setSelectedCategory(value)}
                    options={MENU_CATEGORY_OPTIONS.map((option) => ({
                      value: option.id,
                      label: option.label,
                    }))}
                    prefixIcon={<FilterIcon />}
                    displayEmpty
                  />
                </div>
              </div>
            </div>
            <div className="w-full space-y-4">
              {!isLoading && pagination.totalItems > 0 && (
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-sm font-bold text-slate-500">
                    登録済みメニュー <span className="ml-1 text-slate-400 font-normal">{pagination.totalItems}件</span>
                  </h2>
                </div>
              )}
              {isLoading ? (
                <LoadingProgress />
              ) : menus.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {menus.map((menu) => (
                      <MenuCard
                        key={menu._id}
                        id={menu._id}
                        code={menu._id}
                        name={menu.name}
                        category={menu.category}
                        treatmentTime={menu.treatmentTime}
                        publicStatus={menu.publicStatus}
                        updatedAt={menu.updatedAt}
                        onDelete={() => handleDeleteMenu(menu._id, menu.name)}
                        onCopy={() => handleCopyMenu(menu._id)}
                      />
                    ))}
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                        className={`px-4 py-2 rounded-xl border text-sm font-bold transition-all ${
                          pagination.currentPage === 1
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 active:scale-95'
                        }`}
                      >
                        前へ
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
                          const showPage =
                            pageNum === 1 ||
                            pageNum === pagination.totalPages ||
                            Math.abs(pageNum - pagination.currentPage) <= 1;

                          const showEllipsis =
                            (pageNum === 2 && pagination.currentPage > 3) ||
                            (pageNum === pagination.totalPages - 1 &&
                              pagination.currentPage < pagination.totalPages - 2);

                          if (showEllipsis) {
                            return (
                              <span key={pageNum} className="px-2 text-slate-400 text-sm font-bold">
                                ...
                              </span>
                            );
                          }

                          if (!showPage) return null;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`min-w-[40px] px-3 py-2 rounded-xl border text-sm font-bold transition-all ${
                                pageNum === pagination.currentPage
                                  ? 'bg-blue-btn-primary text-white border-blue-btn-primary shadow-sm active:scale-95'
                                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 active:scale-95'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className={`px-4 py-2 rounded-xl border text-sm font-bold transition-all ${
                          pagination.currentPage === pagination.totalPages
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 active:scale-95'
                        }`}
                      >
                        次へ
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <NoData />
              )}
            </div>
          </div>
        </div>

        <ConfirmDeleteDialog
          open={showDeleteDialog}
          handleClose={() => setShowDeleteDialog(false)}
          confirmTitle="以下のメニューを削除しますか？"
          confirmMessage={`メニュー名 ${selectedMenuName}`}
          confirmTitleAgain="本当に削除してもよろしいでしょうか？"
          deleteCompleteTitle="メニューを削除しました"
          deleteCompleteBtnLabel="メニュー管理へ戻る"
          deleteCompleteAction={() => setShowDeleteDialog(false)}
          deleteAction={confirmDelete}
          confirmCancleLabel="キャンセル"
          confirmCancleAgainLabel="キャンセル"
          confirmLabel="削除"
          confirmAgainLabel="削除"
        />
      </MainContentLayout>
    </DefaultLayout>
  );
}

export default MenuManagementTopPage;
